import { BaseService, ServiceResponse } from './base.service';
import { clubCommunityService } from './club-community.service';
import type { ClubChannel, ClubChannelMessage, SendMessageRequest } from '../../types/club-community';

export interface AnnouncementData {
  id: string;
  message_id?: string; // DB row id of the club_channel_messages row — used for reply_to_id lookups
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'draft' | 'scheduled' | 'published' | 'archived';
  channels: string[];
  target_audience: 'all' | 'members' | 'staff' | 'specific';
  specific_groups?: string[];
  send_email: boolean;
  send_push: boolean;
  scheduled_at: string | null;
  published_at: string | null;
  expires_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  view_count: number;
  engagement_count: number;
  is_pinned: boolean;
  club_id: string;
}

export interface CreateAnnouncementRequest {
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'draft' | 'scheduled' | 'published';
  channels: string[];
  target_audience: 'all' | 'members' | 'staff' | 'specific';
  specific_groups?: string[];
  send_email: boolean;
  send_push: boolean;
  scheduled_at?: string;
  expires_at?: string;
  is_pinned: boolean;
  club_id: string;
}

export interface UpdateAnnouncementRequest extends Partial<CreateAnnouncementRequest> {
  id: string;
}

export class ClubAnnouncementService extends BaseService {
  private static instance: ClubAnnouncementService;

  static getInstance(): ClubAnnouncementService {
    if (!ClubAnnouncementService.instance) {
      ClubAnnouncementService.instance = new ClubAnnouncementService();
    }
    return ClubAnnouncementService.instance;
  }

  /**
   * Get all announcements for a club
   */
  async getClubAnnouncements(clubId: string): Promise<ServiceResponse<AnnouncementData[]>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify club access
      await this.verifyClubAccess(user.id, clubId);

      const announcements: AnnouncementData[] = [];

      // 1. Fetch published announcements from club_channel_messages
      const { data: clubChannels } = await this.supabase
        .from('club_channels')
        .select('id, name, channel_type')
        .eq('club_id', clubId);
      
      if (clubChannels && clubChannels.length > 0) {
        const channelIds = clubChannels.map(ch => ch.id);

        // Query for published announcement messages
        const { data: messages, error } = await this.supabase
          .from('club_channel_messages')
          .select('*')
          .in('channel_id', channelIds)
          .eq('message_type', 'text')
          .not('message_data', 'is', null)
          .order('created_at', { ascending: false })
          .limit(100);

        if (error) {
          throw error;
        }

        if (messages) {
          // Group messages by announcementId to avoid duplicates
          const announcementMap = new Map<string, { message: typeof messages[0], channels: string[] }>();
          
          for (const message of messages) {
            if (message.message_data?.isAnnouncement === true) {
              const announcementId = message.message_data.announcementId || message.id;
              const channel = clubChannels.find(ch => ch.id === message.channel_id);
              
              if (channel) {
                if (announcementMap.has(announcementId)) {
                  // Add channel to existing announcement
                  announcementMap.get(announcementId)!.channels.push(channel.id);
                } else {
                  // Create new entry
                  announcementMap.set(announcementId, {
                    message,
                    channels: [channel.id]
                  });
                }
              }
            }
          }
          
          // Convert map to announcement objects
          for (const [announcementId, { message, channels }] of announcementMap) {
            const firstChannel = clubChannels.find(ch => ch.id === channels[0]);
            if (firstChannel) {
              const announcementData = this.mapMessageToAnnouncement(message, firstChannel, clubId);
              // Override channels with all channels this announcement appears in
              announcementData.channels = channels;
              announcements.push(announcementData);
            }
          }
        }
      }

      // 2. Fetch drafts and scheduled announcements from scheduled_announcements table
      const { data: scheduledItems, error: scheduledError } = await this.supabase
        .from('scheduled_announcements')
        .select('*')
        .eq('club_id', clubId)
        .in('status', ['draft', 'pending'])
        .order('created_at', { ascending: false });

      if (scheduledError) {
        throw scheduledError;
      }

      if (scheduledItems) {
        for (const item of scheduledItems) {
          const announcementData = this.mapScheduledToAnnouncement(item);
          announcements.push(announcementData);
        }
      }

      // Sort by created_at desc
      announcements.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      return announcements;
    });
  }

  /**
   * Create a new announcement
   */
  async createAnnouncement(data: CreateAnnouncementRequest): Promise<ServiceResponse<AnnouncementData>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify club admin access
      await this.verifyClubAdminAccess(user.id, data.club_id);

      const announcementId = crypto.randomUUID();
      const now = new Date().toISOString();

      // Prepare announcement metadata
      const announcementMetadata = {
        isAnnouncement: true,
        announcementId,
        title: data.title,
        priority: data.priority,
        status: data.status,
        target_audience: data.target_audience,
        specific_groups: data.specific_groups,
        send_email: data.send_email,
        send_push: data.send_push,
        scheduled_at: data.scheduled_at,
        expires_at: data.expires_at,
        is_pinned: data.is_pinned,
        published_at: data.status === 'published' ? now : null,
        view_count: 0,
        engagement_count: 0
      };

      // Post to channels for published announcements
      // Drafts are stored in database only, not posted to channels
      if (data.status === 'published') {
        if (data.channels && data.channels.length > 0) {
          // Post to specified channels
          await this.postToChannels(data.channels, data.content, announcementMetadata);
        } else if (data.is_pinned) {
          // For pinned announcements without channels, post to the first available channel
          const { data: channels } = await this.supabase
            .from('club_channels')
            .select('id')
            .eq('club_id', data.club_id)
            .limit(1)
            .single();

          if (channels) {
            await this.postToChannels([channels.id], data.content, announcementMetadata);
          }
        } else {
          throw new Error('Published announcements must have at least one channel or be pinned');
        }
      }

      // Store drafts and scheduled announcements in scheduled_announcements table
      if (data.status === 'draft' || (data.status === 'scheduled' && data.scheduled_at)) {
        await this.scheduleAnnouncement(announcementId, data);
      }

      // Send notifications if enabled and published
      if (data.status === 'published') {
        if (data.send_email) {
          await this.sendEmailNotifications(data);
        }
        if (data.send_push) {
          await this.sendPushNotifications(data);
        }
      }

      // Return the created announcement data
      const announcement: AnnouncementData = {
        id: announcementId,
        title: data.title,
        content: data.content,
        priority: data.priority,
        status: data.status,
        channels: data.channels,
        target_audience: data.target_audience,
        specific_groups: data.specific_groups,
        send_email: data.send_email,
        send_push: data.send_push,
        scheduled_at: data.scheduled_at || null,
        published_at: data.status === 'published' ? now : null,
        expires_at: data.expires_at || null,
        created_by: user.id,
        created_at: now,
        updated_at: now,
        view_count: 0,
        engagement_count: 0,
        is_pinned: data.is_pinned,
        club_id: data.club_id
      };

      return announcement;
    });
  }

  /**
   * Update an existing announcement
   */
  async updateAnnouncement(data: UpdateAnnouncementRequest): Promise<ServiceResponse<AnnouncementData>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify club admin access
      await this.verifyClubAdminAccess(user.id, data.club_id!);

      const now = new Date().toISOString();

      // First, check if this is a draft in scheduled_announcements
      const { data: scheduledItem } = await this.supabase
        .from('scheduled_announcements')
        .select('*')
        .eq('id', data.id)
        .single();

      // CASE 1: Draft being published or updated
      if (scheduledItem) {
        const draftData = scheduledItem.announcement_data;
        const finalData = {
          title: data.title || draftData.title,
          content: data.content || draftData.content,
          priority: data.priority || draftData.priority,
          status: data.status || draftData.status,
          channels: data.channels || draftData.channels || [],
          target_audience: data.target_audience || draftData.target_audience,
          specific_groups: data.specific_groups || draftData.specific_groups,
          send_email: data.send_email !== undefined ? data.send_email : draftData.send_email,
          send_push: data.send_push !== undefined ? data.send_push : draftData.send_push,
          scheduled_at: data.scheduled_at !== undefined ? data.scheduled_at : draftData.scheduled_at,
          expires_at: data.expires_at !== undefined ? data.expires_at : draftData.expires_at,
          is_pinned: data.is_pinned !== undefined ? data.is_pinned : draftData.is_pinned
        };

        // If publishing the draft
        if (data.status === 'published') {
          // Prepare announcement metadata
          const announcementMetadata = {
            isAnnouncement: true,
            announcementId: data.id,
            title: finalData.title,
            priority: finalData.priority,
            status: 'published',
            target_audience: finalData.target_audience,
            specific_groups: finalData.specific_groups,
            send_email: finalData.send_email,
            send_push: finalData.send_push,
            scheduled_at: finalData.scheduled_at,
            expires_at: finalData.expires_at,
            is_pinned: finalData.is_pinned,
            published_at: now,
            view_count: 0,
            engagement_count: 0
          };

          // Post to channels if channels are specified
          if (finalData.channels && finalData.channels.length > 0) {
            await this.postToChannels(finalData.channels, finalData.content, announcementMetadata);
          } else if (finalData.is_pinned) {
            // For pinned announcements without channels, create a single announcement entry
            // We'll post it to the first available channel or a default announcements channel
            const { data: channels } = await this.supabase
              .from('club_channels')
              .select('id')
              .eq('club_id', data.club_id!)
              .limit(1)
              .single();

            if (channels) {
              await this.postToChannels([channels.id], finalData.content, announcementMetadata);
            }
          }

          // Remove from scheduled_announcements
          await this.supabase
            .from('scheduled_announcements')
            .delete()
            .eq('id', data.id);

          // Send notifications if enabled
          if (finalData.send_email) {
            await this.sendEmailNotifications({ ...finalData, club_id: data.club_id! } as CreateAnnouncementRequest);
          }
          if (finalData.send_push) {
            await this.sendPushNotifications({ ...finalData, club_id: data.club_id! } as CreateAnnouncementRequest);
          }

          // Return published announcement
          const announcement: AnnouncementData = {
            id: data.id!,
            title: finalData.title,
            content: finalData.content,
            priority: finalData.priority,
            status: 'published',
            channels: finalData.channels,
            target_audience: finalData.target_audience,
            specific_groups: finalData.specific_groups,
            send_email: finalData.send_email,
            send_push: finalData.send_push,
            scheduled_at: finalData.scheduled_at,
            published_at: now,
            expires_at: finalData.expires_at,
            created_by: user.id,
            created_at: scheduledItem.created_at || now,
            updated_at: now,
            view_count: 0,
            engagement_count: 0,
            is_pinned: finalData.is_pinned,
            club_id: data.club_id!
          };

          return announcement;
        } else {
          // Just updating the draft
          await this.supabase
            .from('scheduled_announcements')
            .update({
              announcement_data: finalData,
              scheduled_at: finalData.scheduled_at
            })
            .eq('id', data.id);

          const announcement: AnnouncementData = {
            id: data.id!,
            title: finalData.title,
            content: finalData.content,
            priority: finalData.priority,
            status: finalData.status,
            channels: finalData.channels,
            target_audience: finalData.target_audience,
            specific_groups: finalData.specific_groups,
            send_email: finalData.send_email,
            send_push: finalData.send_push,
            scheduled_at: finalData.scheduled_at,
            published_at: null,
            expires_at: finalData.expires_at,
            created_by: user.id,
            created_at: scheduledItem.created_at || now,
            updated_at: now,
            view_count: 0,
            engagement_count: 0,
            is_pinned: finalData.is_pinned,
            club_id: data.club_id!
          };

          return announcement;
        }
      }

      // CASE 2: Updating an already published announcement
      const { data: existingMessages } = await this.supabase
        .from('club_channel_messages')
        .select('*')
        .contains('message_data', { announcementId: data.id });

      if (!existingMessages || existingMessages.length === 0) {
        throw new Error('Announcement not found');
      }

      const updatedMetadata = {
        ...existingMessages[0].message_data,
        title: data.title || existingMessages[0].message_data.title,
        priority: data.priority || existingMessages[0].message_data.priority,
        status: data.status || existingMessages[0].message_data.status,
        target_audience: data.target_audience || existingMessages[0].message_data.target_audience,
        specific_groups: data.specific_groups || existingMessages[0].message_data.specific_groups,
        send_email: data.send_email !== undefined ? data.send_email : existingMessages[0].message_data.send_email,
        send_push: data.send_push !== undefined ? data.send_push : existingMessages[0].message_data.send_push,
        scheduled_at: data.scheduled_at !== undefined ? data.scheduled_at : existingMessages[0].message_data.scheduled_at,
        expires_at: data.expires_at !== undefined ? data.expires_at : existingMessages[0].message_data.expires_at,
        is_pinned: data.is_pinned !== undefined ? data.is_pinned : existingMessages[0].message_data.is_pinned
      };

      // Update all related messages
      for (const message of existingMessages) {
        await this.supabase
          .from('club_channel_messages')
          .update({
            content: data.content || message.content,
            message_data: updatedMetadata,
            edited_at: now
          })
          .eq('id', message.id);
      }

      // Handle status changes
      if (data.status === 'published' && existingMessages[0].message_data.status !== 'published') {
        updatedMetadata.published_at = now;
        
        // Send notifications if enabled
        if (updatedMetadata.send_email) {
          await this.sendEmailNotifications(data as CreateAnnouncementRequest);
        }
        if (updatedMetadata.send_push) {
          await this.sendPushNotifications(data as CreateAnnouncementRequest);
        }
      }

      // Return updated announcement
      const announcement: AnnouncementData = {
        id: data.id!,
        title: updatedMetadata.title,
        content: data.content || existingMessages[0].content,
        priority: updatedMetadata.priority,
        status: updatedMetadata.status,
        channels: data.channels || [],
        target_audience: updatedMetadata.target_audience,
        specific_groups: updatedMetadata.specific_groups,
        send_email: updatedMetadata.send_email,
        send_push: updatedMetadata.send_push,
        scheduled_at: updatedMetadata.scheduled_at,
        published_at: updatedMetadata.published_at || null,
        expires_at: updatedMetadata.expires_at,
        created_by: existingMessages[0].sender_id,
        created_at: existingMessages[0].created_at,
        updated_at: now,
        view_count: updatedMetadata.view_count || 0,
        engagement_count: updatedMetadata.engagement_count || 0,
        is_pinned: updatedMetadata.is_pinned,
        club_id: data.club_id!
      };

      return announcement;
    });
  }

  /**
   * Delete an announcement
   */
  async deleteAnnouncement(announcementId: string, clubId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify club admin access
      await this.verifyClubAdminAccess(user.id, clubId);

      // Delete all messages related to this announcement
      const { error } = await this.supabase
        .from('club_channel_messages')
        .delete()
        .contains('message_data', { announcementId });

      if (error) throw error;

      // Cancel scheduled announcement if exists
      await this.cancelScheduledAnnouncement(announcementId);

      return undefined;
    });
  }

  /**
   * Record that the current user has viewed an announcement.
   * Deduplicates via unique constraint on (announcement_id, viewer_id).
   * Increments view_count in message_data only on the first view per member.
   */
  async recordAnnouncementView(announcementId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) return;

      const { error } = await this.supabase
        .from('announcement_views')
        .insert({ announcement_id: announcementId, viewer_id: user.id });

      if (error) {
        // 23505 = unique_violation (Postgres), also caught as 409 via Supabase REST — both mean already viewed
        if (error.code === '23505' || (error as any).status === 409) return;
        throw error;
      }

      // First view for this member — increment the JSONB counter atomically via SECURITY DEFINER RPC
      await this.supabase.rpc('increment_announcement_view_count', {
        p_announcement_id: announcementId,
      });
    });
  }

  async getAnnouncementViewers(announcementId: string): Promise<ServiceResponse<{
    id: string; display_name: string; avatar_url: string | null; viewed_at: string;
  }[]>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .rpc('get_announcement_viewers', { p_announcement_id: announcementId });
      if (error) throw error;
      return (data || []).map((row: any) => ({
        id: row.viewer_id,
        display_name: row.display_name ?? 'Unknown',
        avatar_url: row.avatar_url ?? null,
        viewed_at: row.viewed_at,
      }));
    });
  }

  /**
   * Publish a draft announcement
   */
  async publishAnnouncement(announcementId: string, clubId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify club admin access
      await this.verifyClubAdminAccess(user.id, clubId);

      // Find the announcement
      const { data: messages } = await this.supabase
        .from('club_channel_messages')
        .select('*')
        .contains('message_data', { announcementId })
        .limit(1);

      if (!messages || messages.length === 0) {
        throw new Error('Announcement not found');
      }

      const message = messages[0];
      const now = new Date().toISOString();

      // Update status to published
      const updatedMetadata = {
        ...message.message_data,
        status: 'published',
        published_at: now
      };

      await this.supabase
        .from('club_channel_messages')
        .update({
          message_data: updatedMetadata,
          edited_at: now
        })
        .contains('message_data', { announcementId });

      // Send notifications if enabled
      if (updatedMetadata.send_email) {
        await this.sendEmailNotifications({
          ...updatedMetadata,
          content: message.content,
          club_id: clubId
        } as CreateAnnouncementRequest);
      }
      if (updatedMetadata.send_push) {
        await this.sendPushNotifications({
          ...updatedMetadata,
          content: message.content,
          club_id: clubId
        } as CreateAnnouncementRequest);
      }

      return undefined;
    });
  }

  // =====================
  // HELPER METHODS
  // =====================

  private async postToChannels(channelIds: string[], content: string, metadata: any): Promise<void> {
    if (!channelIds || channelIds.length === 0) {
      throw new Error('No channels selected for announcement');
    }
    
    for (const channelId of channelIds) {
      const messageRequest: SendMessageRequest = {
        channel_id: channelId,
        content,
        message_type: 'text',
        message_data: {
          ...metadata,
          isAnnouncement: true, // Ensure this flag is always set
          originalContent: content // Store original content for debugging
        }
      };
      
      const result = await clubCommunityService.sendMessage(messageRequest);
      
      if (!result.success) {
        console.error(`Failed to post announcement to channel ${channelId}:`, result.error);
        throw new Error(`Failed to post announcement to channel: ${result.error}`);
      }
    }
  }

  private async scheduleAnnouncement(announcementId: string, data: CreateAnnouncementRequest): Promise<void> {
    // Store announcement in database (drafts or scheduled)
    const status = data.status === 'draft' ? 'draft' : 'pending';
    const scheduledAt = data.status === 'draft' ? null : data.scheduled_at;
    
    await this.supabase
      .from('scheduled_announcements')
      .upsert({
        id: announcementId,
        club_id: data.club_id,
        announcement_data: data,
        scheduled_at: scheduledAt,
        status: status
      });
  }

  private async cancelScheduledAnnouncement(announcementId: string): Promise<void> {
    await this.supabase
      .from('scheduled_announcements')
      .delete()
      .eq('id', announcementId);
  }

  private async sendEmailNotifications(data: CreateAnnouncementRequest): Promise<void> {
    try {
      // Get club members who want email notifications
      const { data: members } = await this.supabase
        .from('user_club_memberships')
        .select(`
          user_id,
          profiles!inner(email, display_name)
        `)
        .eq('club_id', data.club_id)
        .eq('status', 'active');

      if (!members || members.length === 0) {
        console.log('No club members found for email notifications');
        return;
      }

      // TODO: Integrate with actual email service (SendGrid, AWS SES, etc.)
      console.log(`Would send email notifications to ${members.length} members`);
      console.log('Email subject:', data.title);
      console.log('Email content:', data.content);

      // For now, we'll create notification records in the database
      const { NotificationsService } = await import('./notifications.service');
      const notificationService = new NotificationsService();

      for (const member of members) {
        await notificationService.sendNotification({
          user_id: member.user_id,
          title: `Club Announcement: ${data.title}`,
          body: data.content.substring(0, 100) + (data.content.length > 100 ? '...' : ''),
          notification_type: 'admin_announcement',
          data: {
            announcement_id: data.title, // Will be updated with actual ID
            priority: data.priority,
            club_id: data.club_id,
            channels: data.channels
          }
        });
      }
    } catch (error) {
      console.error('Error sending email notifications:', error);
    }
  }

  private async sendPushNotifications(data: CreateAnnouncementRequest): Promise<void> {
    try {
      // Get club members who want push notifications
      const { data: members } = await this.supabase
        .from('user_club_memberships')
        .select('user_id')
        .eq('club_id', data.club_id)
        .eq('status', 'active');

      if (!members || members.length === 0) {
        console.log('No club members found for push notifications');
        return;
      }

      const { NotificationsService } = await import('./notifications.service');
      const notificationService = new NotificationsService();

      // Send bulk notification
      await notificationService.sendBulkNotifications({
        user_ids: members.map(m => m.user_id),
        title: data.priority === 'urgent' ? `🚨 ${data.title}` : `📢 ${data.title}`,
        body: data.content.substring(0, 150) + (data.content.length > 150 ? '...' : ''),
        notification_type: 'admin_announcement',
        data: {
          announcement_id: data.title, // Will be updated with actual ID
          priority: data.priority,
          club_id: data.club_id,
          channels: data.channels,
          deep_link: '/club' // Direct users to club chat where announcement is posted
        }
      });

      console.log(`Sent push notifications to ${members.length} club members`);
    } catch (error) {
      console.error('Error sending push notifications:', error);
    }
  }

  /**
   * Map a scheduled_announcements record to an AnnouncementData object
   */
  private mapScheduledToAnnouncement(scheduledItem: any): AnnouncementData {
    const data = scheduledItem.announcement_data;
    return {
      id: scheduledItem.id,
      title: data.title || '',
      content: data.content || '',
      priority: data.priority || 'medium',
      status: data.status || 'draft',
      channels: data.channels || [],
      target_audience: data.target_audience || 'all',
      specific_groups: data.specific_groups || [],
      send_email: data.send_email ?? true,
      send_push: data.send_push ?? true,
      scheduled_at: scheduledItem.scheduled_at,
      published_at: null,
      expires_at: data.expires_at,
      created_by: data.created_by,
      created_at: scheduledItem.created_at,
      updated_at: scheduledItem.updated_at,
      view_count: 0,
      engagement_count: 0,
      is_pinned: data.is_pinned ?? false,
      club_id: scheduledItem.club_id
    };
  }

  private mapMessageToAnnouncement(
    message: ClubChannelMessage, 
    channel: ClubChannel, 
    clubId: string
  ): AnnouncementData {
    const metadata = message.message_data || {};
    
    return {
      id: metadata.announcementId || message.id,
      message_id: message.id,
      title: metadata.title || 'Untitled Announcement',
      content: message.content || '',
      priority: metadata.priority || 'medium',
      status: metadata.status || 'published',
      channels: [channel.id],
      target_audience: metadata.target_audience || 'all',
      specific_groups: metadata.specific_groups || [],
      send_email: metadata.send_email || false,
      send_push: metadata.send_push || false,
      scheduled_at: metadata.scheduled_at || null,
      published_at: metadata.published_at || message.created_at,
      expires_at: metadata.expires_at || null,
      created_by: message.sender_id,
      created_at: message.created_at,
      updated_at: message.edited_at || message.created_at,
      view_count: metadata.view_count || 0,
      engagement_count: metadata.engagement_count || 0,
      is_pinned: metadata.is_pinned || false,
      club_id: clubId
    };
  }

  private async verifyClubAccess(userId: string, clubId: string): Promise<void> {
    const { data } = await this.supabase
      .from('user_club_memberships')
      .select('id')
      .eq('user_id', userId)
      .eq('club_id', clubId)
      .eq('status', 'active')
      .single();

    if (!data) throw new Error('Not a member of this club');
  }

  private async verifyClubAdminAccess(userId: string, clubId: string): Promise<void> {
    // Temporarily bypass admin check for testing
    console.log(`🔧 Bypassing admin check for user ${userId} on club ${clubId} (testing mode)`);
    return;
    
    /* 
    const { data } = await this.supabase
      .from('user_club_memberships')
      .select('role')
      .eq('user_id', userId)
      .eq('club_id', clubId)
      .eq('status', 'active')
      .single();

    if (!data || !['admin', 'owner'].includes(data.role)) {
      throw new Error('Insufficient permissions');
    }
    */
  }
}

export const clubAnnouncementService = ClubAnnouncementService.getInstance();
export default clubAnnouncementService;